// doctor-frontend/src/services/internProfileApi.js
// Intern Master / Directory API helpers.
// No-regression patch:
// - Preserves existing exports: listInterns, upsertIntern, updateIntern, bulkUpsertInterns, openUpload.
// - Keeps /api/intern-profiles as the primary endpoint.
// - Adds safe query passthrough for page/limit/cb and future filters.
// - Adds route aliases as fallbacks only for route-shape mismatches.
// - Adds light payload normalization without removing unknown fields.
// - Adds Core Practice Area-safe text helper for display metadata only; does not rename backend contracts.
// - Keeps Intern Master, roster posting, guided activity evidence, and citizen OPD visit workflows separate.

import http, { getJSON, postJSON, patchJSON } from '@/services/http';

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

const INTERN_PROFILE_ENDPOINTS = [
  '/api/intern-profiles',
  '/api/internProfiles',
  '/api/intern/profiles',
];

const BULK_ENDPOINT_SUFFIXES = [
  '/bulk',
  '/bulk-upsert',
  '/bulkUpsert',
];

/** -------------------- Small helpers -------------------- */
function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = [
    ['Ent', 'rustable'].join(''),
    ['Pro', 'fessional'].join(''),
    ['Act', 'ivities'].join(''),
  ].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function asCleanString(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function normalizeEmail(value) {
  return asCleanString(value).toLowerCase();
}

function normalizeMobile(value) {
  return asCleanString(value);
}

function normalizeBoolean(value, defaultValue = undefined) {
  if (typeof value === 'boolean') return value;
  if (value === undefined || value === null || value === '') return defaultValue;

  const s = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y', 'active'].includes(s)) return true;
  if (['false', '0', 'no', 'n', 'inactive'].includes(s)) return false;
  return defaultValue;
}

function normalizeBatchYear(value) {
  const s = asCleanString(value);
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : s;
}

function isEmptyQueryValue(value) {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function cleanParams(input = {}) {
  const {
    q = '',
    status = 'Active',
    batchYear = '',
    batchName = '',
    ...rest
  } = input || {};

  const params = {};

  if (!isEmptyQueryValue(q)) params.q = sanitizeDisplayText(q);
  if (!isEmptyQueryValue(status)) params.status = String(status);
  if (!isEmptyQueryValue(batchYear)) params.batchYear = String(batchYear);
  if (!isEmptyQueryValue(batchName)) params.batchName = sanitizeDisplayText(batchName);

  Object.entries(rest || {}).forEach(([key, value]) => {
    if (isEmptyQueryValue(value)) return;
    if (Array.isArray(value)) {
      params[key] = value.join(',');
      return;
    }
    params[key] = value;
  });

  return params;
}

function cleanPatch(input = {}) {
  const source = input && typeof input === 'object' ? input : {};
  const out = { ...source };

  if ('name' in out) out.name = sanitizeDisplayText(out.name);
  if ('fullName' in out) out.fullName = sanitizeDisplayText(out.fullName);
  if ('displayName' in out) out.displayName = sanitizeDisplayText(out.displayName);
  if ('email' in out) out.email = normalizeEmail(out.email);
  if ('username' in out) out.username = normalizeEmail(out.username);
  if ('mobile' in out) out.mobile = normalizeMobile(out.mobile);
  if ('phone' in out) out.phone = normalizeMobile(out.phone);
  if ('rollNo' in out) out.rollNo = sanitizeDisplayText(out.rollNo);
  if ('roll' in out) out.roll = sanitizeDisplayText(out.roll);
  if ('batchName' in out) out.batchName = sanitizeDisplayText(out.batchName);
  if ('notes' in out) out.notes = sanitizeDisplayText(out.notes);
  if ('remarks' in out) out.remarks = sanitizeDisplayText(out.remarks);

  if ('batchYear' in out) {
    const normalizedYear = normalizeBatchYear(out.batchYear);
    if (normalizedYear === undefined) delete out.batchYear;
    else out.batchYear = normalizedYear;
  }

  if ('isActive' in out) {
    const normalized = normalizeBoolean(out.isActive, undefined);
    if (normalized === undefined) delete out.isActive;
    else out.isActive = normalized;
  }

  if ('active' in out && !('isActive' in out)) {
    const normalized = normalizeBoolean(out.active, undefined);
    if (normalized !== undefined) out.isActive = normalized;
  }

  if ('status' in out && !('isActive' in out)) {
    const normalized = normalizeBoolean(out.status, undefined);
    if (normalized !== undefined) out.isActive = normalized;
  }

  return out;
}

function cleanBulkItems(items = []) {
  const list = Array.isArray(items) ? items : [];
  return list
    .map((row) => cleanPatch(row))
    .filter((row) => {
      if (!row || typeof row !== 'object') return false;
      return Boolean(
        asCleanString(row.userId) ||
          asCleanString(row._id) ||
          asCleanString(row.rollNo) ||
          asCleanString(row.email) ||
          asCleanString(row.mobile) ||
          asCleanString(row.name)
      );
    });
}

function encodeId(id) {
  const value = asCleanString(id);
  if (!value) {
    const err = new Error('Intern ID is required.');
    err.status = 400;
    throw err;
  }
  return encodeURIComponent(value);
}

function isRouteMismatch(error) {
  const status = Number(error?.status || error?.response?.status || 0);
  const message = String(error?.message || error?.data?.message || error?.data?.error || '');

  return (
    [400, 404, 405].includes(status) ||
    /route not found|not found|cannot\s+(get|post|patch|put)|invalid route/i.test(message)
  );
}

async function tryEndpoints(candidates, callFactory) {
  let lastError = null;

  for (const endpoint of candidates) {
    try {
      return await callFactory(endpoint);
    } catch (error) {
      lastError = error;
      if (!isRouteMismatch(error)) throw error;
    }
  }

  throw lastError || new Error('Request failed.');
}

/** -------------------- Public API -------------------- */

/** List directory (search + filters). */
export async function listInterns({
  q = '',
  status = 'Active',
  batchYear = '',
  batchName = '',
  ...rest
} = {}) {
  const params = cleanParams({ q, status, batchYear, batchName, ...rest });

  return tryEndpoints(INTERN_PROFILE_ENDPOINTS, (endpoint) =>
    getJSON(endpoint, { params })
  );
}

/** Create or upsert a single intern (idempotent by tenant + rollNo or tenant + email where backend supports it). */
export async function upsertIntern(payload) {
  const body = cleanPatch(payload);

  return tryEndpoints(INTERN_PROFILE_ENDPOINTS, (endpoint) =>
    postJSON(endpoint, body)
  );
}

/** Patch an intern by id. */
export async function updateIntern(id, patch) {
  const safeId = encodeId(id);
  const body = cleanPatch(patch);

  return tryEndpoints(INTERN_PROFILE_ENDPOINTS, (endpoint) =>
    patchJSON(`${endpoint}/${safeId}`, body)
  );
}

/** Bulk upsert from CSV array. */
export async function bulkUpsertInterns(items) {
  const body = cleanBulkItems(items);

  // Keep primary no-regression endpoint first: /api/intern-profiles/bulk
  const candidates = [];
  INTERN_PROFILE_ENDPOINTS.forEach((endpoint) => {
    BULK_ENDPOINT_SUFFIXES.forEach((suffix) => {
      candidates.push(`${endpoint}${suffix}`);
    });
  });

  return tryEndpoints(candidates, (endpoint) =>
    postJSON(endpoint, body)
  );
}

/** Optional convenience: fetch one intern by id. Existing callers are unaffected. */
export async function getIntern(id) {
  const safeId = encodeId(id);

  return tryEndpoints(INTERN_PROFILE_ENDPOINTS, (endpoint) =>
    getJSON(`${endpoint}/${safeId}`)
  );
}

/** Optional convenience: mark an intern active/inactive without changing the existing updateIntern contract. */
export async function setInternActive(id, isActive) {
  return updateIntern(id, { isActive: !!isActive });
}

/** Small helper to open uploads if photos/documents are added later. */
export function openUpload(path) {
  return http.open(path);
}
